export type SiteInfo = {
  name: string
  email: string
  phone?: string
  tagline: string
  baseUrl: string
}

export type NavItem = {
  label: string
  path: string
}

export type Stat = {
  value: number
  suffix: string
  label: string
}

export type ProgramCard = {
  title: string
  body: string
}

export type Partner = {
  name: string
  relationship: string
  logo?: string
}

export type HybridSpecRow =
  | { label: string; value: string }
  | { heading: string }

export type Hybrid = {
  code: string
  type: 'White Maize' | 'Yellow Maize'
  maturity: string
  days: string
  image: string
  strengths: string[]
  note: string
  datasheet?: string
  specs?: HybridSpecRow[]
}

export type HomeContent = {
  hero: {
    video?: {
      mp4: string
      webm?: string
      poster: string
    }
    fallbackImage: string
    eyebrow: string
    headline: string
    highlight: string
    subheadline: string
    primaryCta: { label: string; path: string }
    secondaryCta: { label: string; path: string }
  }
  stats: Stat[]
  intro: {
    eyebrow: string
    headline: string
    highlight: string
    body: string
    image: string
    imageCaption: string
    cta: { label: string; path: string }
  }
  program: {
    eyebrow: string
    title: string
    body: string
    cta: { label: string; path: string }
    cards: ProgramCard[]
  }
  interlude: {
    image: string
    caption: string
  }
  partners: {
    eyebrow: string
    title: string
    body: string
    items: Partner[]
    cta: { label: string; path: string }
  }
  quote: {
    text: string
    attribution: string
  }
  cta: {
    eyebrow: string
    title: string
    body: string
    primary: { label: string; path: string }
    secondary: { label: string; path: string }
  }
}

export type HybridsContent = {
  hero: {
    eyebrow: string
    title: string
    body: string
  }
  philosophy: {
    eyebrow: string
    body: string
  }
  portfolio: {
    eyebrow: string
    title: string
    hybrids: Hybrid[]
    footerNote: string
    cta: { label: string; path: string }
  }
  quality: {
    image: string
    imageCaption: string
    eyebrow: string
    title: string
    body: string
  }
}

export const site: SiteInfo = {
  name: 'Hylix Genetics',
  email: 'admin@hylix.co.za',
  tagline: 'Independent non-GMO maize genetics. 25 years. 100+ lines.',
  baseUrl: 'https://hylix-za.pages.dev',
}

export const nav: NavItem[] = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Hybrids', path: '/hybrids' },
  { label: 'Breeding Program', path: '/breeding-program' },
  { label: 'Partners', path: '/partners' },
  { label: 'Work With Us', path: '/#contact' },
]

export const home: HomeContent = {
  hero: {
    // Replace with real video once uploaded:
    // video: {
    //   mp4: '/assets/videos/hero.mp4',
    //   webm: '/assets/videos/hero.webm',
    //   poster: '/assets/videos/hero-poster.jpg',
    // },
    fallbackImage: '/images/hero-field.jpg',
    eyebrow: 'Independent maize breeder — South Africa',
    headline: 'Independent maize genetics.',
    highlight: '25 years, zero GMO.',
    subheadline:
      'Hylix has developed 100+ unique maize lines and commercial hybrids for Southern Africa — entirely non-GMO, locally adapted, and backed by 25 seasons of field results.',
    primaryCta: { label: 'Explore the Program', path: '/breeding-program' },
    secondaryCta: { label: 'Licensing & Partnership', path: '/#work-with-us' },
  },
  stats: [
    { value: 25, suffix: '+', label: 'Years of continuous breeding' },
    { value: 100, suffix: '+', label: 'Unique inbred lines developed' },
    { value: 4, suffix: '', label: 'Commercial hybrid families — HX series' },
    { value: 0, suffix: '', label: 'GMO. Conventional only' },
  ],
  intro: {
    eyebrow: 'Who is Hylix',
    headline: 'Independently bred. Scientifically rigorous.',
    highlight: 'Zero GMO.',
    body:
      'For over 25 years, Hylix has operated outside the corporate seed system — developing maize hybrids through conventional breeding methods adapted to Southern Africa\'s diverse growing conditions. Every line is the product of field selection, genetic discipline, and patience.',
    image: '/images/breeding-hands.jpg',
    imageCaption: 'Hand pollination — breeding nursery',
    cta: { label: 'Read our story →', path: '/about' },
  },
  program: {
    eyebrow: 'The Breeding Program',
    title: 'A program of real depth',
    body:
      'Hylix maintains over 100 inbred lines across multiple heterotic groups — the genetic foundation for a pipeline of white and yellow maize hybrids designed for Southern African conditions.',
    cta: { label: 'View Full Program', path: '/breeding-program' },
    cards: [
      {
        title: 'Germplasm Library',
        body: 'Over 100 fixed inbred lines maintained across multiple heterotic groups — white and yellow pools with broad, documented genetic diversity.',
      },
      {
        title: 'Adaptation Focus',
        body: 'Every line selected under Southern African conditions: Highveld dryland, irrigation schemes, and marginal-rainfall zones.',
      },
      {
        title: 'Active Pipeline',
        body: 'New hybrid combinations advance through testcross and multi-location yield trials every season. Four commercial families released to date.',
      },
    ],
  },
  interlude: {
    image: '/images/trial-plots.jpg',
    caption: 'Multi-location yield trials — South Africa',
  },
  partners: {
    eyebrow: 'Partners',
    title: 'Who runs Hylix genetics',
    body:
      'Hylix hybrids and germplasm have been licensed, produced, and sold by some of Southern Africa\'s most recognised seed brands.',
    items: [
      { name: 'Delta Seed', relationship: 'Primary licensee' },
      { name: 'Sensako', relationship: 'Collaborative development' },
      { name: 'Linkseeds', relationship: 'Strategic partnership' },
      { name: 'Maxum Seeds', relationship: 'Licensed production' },
    ],
    cta: { label: 'See all partnerships →', path: '/partners' },
  },
  quote: {
    text: 'Breeding takes time. You have to listen to the land, and let the plant teach you something new.',
    attribution: 'Founder, Hylix Genetics',
  },
  cta: {
    eyebrow: 'Work With Hylix',
    title: 'Proven genetics, ready to license',
    body:
      'The Hylix breeding program represents decades of independent genetic development, a full commercial pipeline, and a commercial track record across Southern Africa. For licensing, germplasm access, or development partnerships — start a conversation with us directly.',
    primary: { label: 'Discuss Licensing', path: '/#contact' },
    secondary: { label: 'Request Germplasm Information', path: '/#contact' },
  },
}

export const hybrids: HybridsContent = {
  hero: {
    eyebrow: 'Commercial Hybrids',
    title: 'Four families. All non-GMO.',
    body: 'The HX series — developed for Southern African production conditions, grown and sold through licensed partners, and backed by 25 seasons of selection data.',
  },
  philosophy: {
    eyebrow: 'Why Conventional',
    body:
      'Hylix believes conventional breeding remains the most sustainable path for African agriculture. Our hybrids compete on yield, stress tolerance, and adaptation — without genetic modification. This preserves genetic diversity, supports soil health, and gives farmers seed they can save and replant.',
  },
  portfolio: {
    eyebrow: 'The HX Series',
    title: 'The portfolio',
    hybrids: [
      {
        code: 'HX11',
        type: 'White Maize',
        maturity: 'Early-Medium',
        days: '±115 days',
        image: '/images/maize-ear.jpg',
        strengths: ['Early seedling vigour', 'Good dryland adaptation', 'Standability under stress'],
        note: 'A dependable early-medium white hybrid for dryland production.',
      },
      {
        code: 'HX33',
        type: 'White Maize',
        maturity: 'Medium',
        days: '±125 days',
        image: '/images/hero-field.jpg',
        strengths: ['Stress tolerance', 'Broader adaptation', 'Density flexibility'],
        note: 'The broader-adaptation white companion to HX11, for variable seasons.',
      },
      {
        code: 'HX22',
        type: 'Yellow Maize',
        maturity: 'Early-Medium',
        days: '±115 days',
        image: '/images/trial-plots.jpg',
        strengths: ['Root strength', 'Marginal soils', 'Early-medium maturity'],
        note: 'A yellow hybrid built for tougher ground and feed markets.',
      },
      {
        code: 'HX44',
        type: 'Yellow Maize',
        maturity: 'Medium',
        days: '±125 days',
        image: '/images/seed-archive.jpg',
        strengths: ['Long cob', 'Yield stability', 'Broader adaptation'],
        note: 'The yield-stability yellow for high-potential and irrigation land.',
      },
    ],
    footerNote:
      'Complete spec sheets and trial data are available to qualified seed companies on request.',
    cta: { label: 'Request Full Spec Sheets', path: '/#contact' },
  },
  quality: {
    image: '/images/maize-ear.jpg',
    imageCaption: 'Grain quality — white flint-dent type',
    eyebrow: 'Grain Quality',
    title: 'Bred for the mill, not just the yield map',
    body:
      'Beyond agronomics, Hylix selects for grain quality — kernel hardness, test weight, and milling performance in white lines; feed value in yellow. Final quality metrics are being compiled into the spec sheets for each hybrid.',
  },
}

// Helper to turn the flat specRows pattern into typed rows once real data arrives.
export function makeHybridSpecs(h: Hybrid, rows: Array<[string, string | null]>): HybridSpecRow[] {
  return rows.map(([label, value]) => {
    if (value === null) {
      return { heading: label }
    }
    let resolved = value
    if (value === 'code') resolved = h.code
    if (value === 'type') resolved = h.type
    if (value === 'maturity') resolved = `${h.maturity} (${h.days})`
    return { label, value: resolved }
  })
}
